import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Box from '@mui/material/Box';
import { LIGHT_GRAY, BG_GRAY } from '../../common/colors';
import { slugify } from '../../common/toc';

const textOf = (children) => React.Children.toArray(children)
  .map(child => typeof child === 'string' ? child : (child.props && textOf(child.props.children)) || '')
  .join('');

// Headings carry slugified ids so the table of contents can anchor-link
// to them (scroll-margin-top keeps them clear of the sticky header).
const heading = (Tag) => {
  const Heading = ({ children }) => <Tag id={slugify(textOf(children))}>{children}</Tag>;
  return Heading;
};

// Images whose alt text ends in "|left" (e.g. author headshots) render
// small and floated left, with surrounding paragraphs wrapping beside them.
const MarkdownImage = ({ src, alt }) => {
  const floatLeft = alt?.endsWith('|left');
  if (!floatLeft)
    return <img src={src} alt={alt} />;
  return (
    <Box
      component="img"
      src={src}
      alt={alt.slice(0, -'|left'.length)}
      sx={{ float: 'left', clear: 'left', width: { xs: '35%', sm: 200 }, mr: 3, mb: 1, borderRadius: '8px' }}
    />
  );
};

// External links (http/https) open in a new tab; in-page anchors (e.g.
// TOC heading links) and relative links stay in the same tab.
const MarkdownLink = ({ href, children, ...rest }) => {
  const external = /^https?:\/\//.test(href || '');
  return (
    <a href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})} {...rest}>
      {children}
    </a>
  );
};

// A table too wide for a phone scrolls sideways inside its own box instead
// of widening the page.
const MarkdownTable = ({ children }) => (
  <Box sx={{ overflowX: 'auto', my: 2 }}>
    <table>{children}</table>
  </Box>
);

const COMPONENTS = { h1: heading('h1'), h2: heading('h2'), h3: heading('h3'), h4: heading('h4'), img: MarkdownImage, a: MarkdownLink, table: MarkdownTable };

// Raw HTML renders as literal text, except <br>: a GFM table cell has no
// other way to break a line, so the rehype step turns it into a real break.
const BR = /^<br\s*\/?>$/i;
const rehypeBr = () => (tree) => {
  const walk = (node) => node.children?.forEach((child, i) => {
    if (child.type === 'raw' && BR.test(child.value))
      node.children[i] = { type: 'element', tagName: 'br', properties: {}, children: [] };
    else
      walk(child);
  });
  walk(tree);
};

// Markdown body renderer (react-markdown + GitHub-flavored markdown),
// styled to the OCL type scale.
const Markdown = ({ children }) => (
  <Box
    sx={{
      color: 'surface.contrastText',
      lineHeight: 1.7,
      // A long URL breaks rather than running off a phone screen.
      overflowWrap: 'break-word',
      '& h1': { clear: 'both', scrollMarginTop: '96px' },
      '& h2': { color: 'surface.dark', fontSize: '1.75rem', fontWeight: 700, mt: 2, mb: 1, scrollMarginTop: '96px', clear: 'both' },
      '& h3': { color: 'surface.dark', fontSize: '1.375rem', fontWeight: 700, mt: 2, mb: 1, scrollMarginTop: '96px' },
      '& h4': { color: 'surface.dark', fontSize: '1.125rem', fontWeight: 700, mt: 2, mb: 1, scrollMarginTop: '96px' },
      '& a': { color: 'primary.main' },
      '& img': { maxWidth: '100%', borderRadius: '8px', border: '2px solid lightgray' },
      '& blockquote': {
        borderLeft: '4px solid',
        borderColor: 'primary.main',
        backgroundColor: BG_GRAY,
        m: 0,
        my: 2,
        px: 2.5,
        py: 0.5,
        borderRadius: '0 8px 8px 0',
      },
      '& pre': { backgroundColor: BG_GRAY, p: 2, borderRadius: '8px', overflowX: 'auto' },
      '& code': { backgroundColor: BG_GRAY, px: 0.5, borderRadius: '4px', fontSize: '0.9em' },
      // Phones get MUI's table cell size (body2), so a 3–4 column table of
      // prose fits without scrolling when its longest words allow.
      '& table': { borderCollapse: 'collapse', width: '100%', fontSize: { xs: '0.875rem', sm: '1rem' } },
      '& th, & td': { border: `1px solid ${LIGHT_GRAY}`, px: { xs: 1, sm: 1.5 }, py: 1, textAlign: 'left', verticalAlign: 'top' },
      '& th': { backgroundColor: BG_GRAY },
    }}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeBr]} components={COMPONENTS}>{children}</ReactMarkdown>
  </Box>
);

export default Markdown;
